/**
 * The camera viewfinder (ASSET-4).
 *
 * Opened from a serial field, hands back a string, and otherwise knows nothing
 * about what the serial is for — so the install register and the "what is
 * this?" lookup share one of these rather than growing two.
 *
 * What it is careful about, in the order it matters on a ladder:
 *
 * **It never offers what it cannot do.** `readiness()` is checked before the
 * button renders, and a device without a camera gets no button and a line
 * saying why — because the fix is usually an address, not a phone.
 *
 * **The download is asked for, not assumed.** On a browser with no built-in
 * decoder, scanning costs close to a megabyte of WebAssembly. That is fine
 * once over the yard's wifi and rude over a tech's mobile data on a job, so
 * the first scan on such a phone asks first and says how big it is.
 *
 * **It stops the camera on every path out.** A viewfinder left running is a
 * green light on the phone, a flat battery by three o'clock, and the single
 * most common thing to get wrong here. The stream is torn down on success, on
 * cancel, on error and on unmount.
 *
 * **It reads continuously and confirms.** A label scanned once at an angle
 * decodes wrong often enough to matter when the result goes straight into a
 * register, so the same value has to come back twice before it counts. That
 * costs a tenth of a second and removes a class of mistake nobody would catch
 * until a warranty claim.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { CAMERA, type Detector, detector, readiness, refusal, serialFrom } from "../lib/scanner";

/** How sure is sure: the same read twice running, which costs a frame. */
const AGREEMENTS = 2;

type Phase = { at: "asking" } | { at: "starting" } | { at: "reading" } | { at: "failed"; why: string };

export function SerialScanner({
  onSerial,
  onClose,
  what = "serial",
}: {
  onSerial: (serial: string) => void;
  onClose: () => void;
  /** What the tech is pointing at, for the instruction line. */
  what?: string;
}) {
  const state = readiness();
  const [phase, setPhase] = useState<Phase>(state.ok && state.needsDecoder ? { at: "asking" } : { at: "starting" });
  const video = useRef<HTMLVideoElement | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const live = useRef(true);

  const stop = useCallback(() => {
    live.current = false;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);

  // Every way out of this component goes through here. Unmount included: a
  // tech who hits the browser's back button must not leave the camera on.
  useEffect(() => stop, [stop]);

  const begin = useCallback(async () => {
    setPhase({ at: "starting" });
    live.current = true;
    let decoder: Detector;
    let media: MediaStream;
    try {
      // Both at once: the decoder may be a download and the camera a
      // permission prompt, and doing them in series means the prompt appears
      // after a wait for no reason the tech can see.
      [decoder, media] = await Promise.all([detector(), navigator.mediaDevices.getUserMedia(CAMERA)]);
    } catch (error) {
      setPhase({ at: "failed", why: refusal(error) });
      return;
    }
    if (!live.current) {
      media.getTracks().forEach((t) => t.stop());
      return;
    }

    stream.current = media;
    const element = video.current;
    if (element) {
      element.srcObject = media;
      // iOS plays a video fullscreen in its own player without this, which
      // takes the page away and the scan with it.
      element.setAttribute("playsinline", "true");
      await element.play().catch(() => undefined);
    }
    setPhase({ at: "reading" });

    let last = "";
    let agreed = 0;
    const read = async () => {
      if (!live.current || !video.current) return;
      try {
        const found = await decoder.detect(video.current);
        const serial = serialFrom(found[0]?.rawValue ?? "");
        if (serial) {
          agreed = serial === last ? agreed + 1 : 1;
          last = serial;
          if (agreed >= AGREEMENTS) {
            stop();
            onSerial(serial);
            return;
          }
        }
      } catch {
        // A frame that will not decode is the normal case, not an error —
        // most frames are the tech still moving the phone.
      }
      if (live.current) requestAnimationFrame(() => void read());
    };
    void read();
  }, [onSerial, stop]);

  useEffect(() => {
    if (phase.at === "starting") void begin();
    // Only on entering `starting`; `begin` restarts it deliberately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.at]);

  const close = () => {
    stop();
    onClose();
  };

  if (!state.ok) {
    return (
      <Shell onClose={close} title="Can't scan here">
        <p className="notice notice-warn" role="status">
          {state.reason}
        </p>
        <p className="tiny muted">Typing the serial always works, and so does a hand-held scanner.</p>
      </Shell>
    );
  }

  return (
    <Shell onClose={close} title={`Scan the ${what}`}>
      {phase.at === "asking" && (
        <div className="stack">
          <p className="notice notice-info" role="status">
            This phone needs Keystone's barcode reader before it can scan — about a megabyte, once. Worth doing on wifi
            rather than out on a job.
          </p>
          <div className="btn-row">
            <button type="button" className="btn btn-primary" onClick={() => setPhase({ at: "starting" })}>
              Get it and scan
            </button>
            <button type="button" className="btn" onClick={close}>
              I'll type it
            </button>
          </div>
        </div>
      )}

      {phase.at === "failed" && (
        <div className="stack">
          <p className="notice notice-crit" role="alert">
            {phase.why}
          </p>
          <div className="btn-row">
            <button type="button" className="btn" onClick={() => void begin()}>
              Try again
            </button>
            <button type="button" className="btn" onClick={close}>
              I'll type it
            </button>
          </div>
        </div>
      )}

      {(phase.at === "starting" || phase.at === "reading") && (
        <div className="viewfinder">
          <video ref={video} className="viewfinder-feed" muted playsInline autoPlay />
          <div className="viewfinder-frame" aria-hidden="true" />
          <p className="viewfinder-hint" role="status">
            {phase.at === "starting" ? "Starting the camera…" : "Hold the label inside the box."}
          </p>
        </div>
      )}
    </Shell>
  );
}

function Shell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  // Escape closes it, because a camera overlay that traps someone is worse
  // than one that does not open.
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  return (
    <>
      <button type="button" className="scan-scrim" aria-label="Close the scanner" onClick={onClose} />
      <div className="scan-panel" role="dialog" aria-modal="true" aria-label={title}>
        <div className="scan-head">
          <strong>{title}</strong>
          <button type="button" className="link-button" onClick={onClose}>
            Close
          </button>
        </div>
        {children}
      </div>
    </>
  );
}

/**
 * Whether to offer scanning at all on this device.
 *
 * Asked by the field as well as by the button, so a field that cannot scan
 * does not also reserve the room a scan button would have taken — a "Scan"
 * button that opens an apology is worse than no button, and an empty gap
 * where one would have been is worse than a full-width input.
 */
export function canScan(): boolean {
  return readiness().ok;
}

/** The button that opens one, shown only where scanning is possible. */
export function ScanButton({ onClick, label = "Scan" }: { onClick: () => void; label?: string }) {
  if (!canScan()) return null;
  return (
    <button type="button" className="link-button" onClick={onClick}>
      {label}
    </button>
  );
}
